import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MessageCircle, Instagram, Send, MapPin } from "lucide-react";
import { toast } from "sonner";
import {
  CONTACT,
  whatsappLink,
  instagramLink,
  gmailLink,
  telLink,
} from "../lib/contact";

const channels = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    handle: "+91 96840 98287",
    icon: MessageCircle,
    href: whatsappLink(),
  },
  {
    id: "instagram",
    label: "Instagram",
    handle: "@sttavannn.7708",
    icon: Instagram,
    href: instagramLink(),
  },
  {
    id: "gmail",
    label: "Email",
    handle: CONTACT.email,
    icon: Mail,
    href: gmailLink(),
  },
  {
    id: "phone",
    label: "Phone",
    handle: "+91 96840 98287",
    icon: Phone,
    href: telLink(),
  },
];

export const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) {
      toast.error("Please add your name and a message.");
      return;
    }
    const fullMsg = `Hi Velora, I'm ${name}${
      email ? ` (${email})` : ""
    }.\n\n${message}`;
    const url = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(fullMsg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp with your pre-filled message…");
    setName("");
    setEmail("");
    setMessage("");
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-14">
          <div className="text-xs sm:text-sm tracking-[0.4em] text-[rgb(182,245,0)] font-mono-alt uppercase mb-4">
            Get In Touch
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
            Let&apos;s Build Something{" "}
            <span className="text-[rgb(182,245,0)]">Together</span>
          </h2>
          <p className="mt-5 text-white/60 max-w-2xl mx-auto">
            Reach out in one click — your message is already pre-filled. No
            typing required.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* CHANNELS */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-4"
          >
            {channels.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.id}
                  data-testid={`contact-channel-${c.id}`}
                  href={c.href}
                  target={c.id === "phone" ? "_self" : "_blank"}
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between surface rounded-2xl p-5"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[rgb(182,245,0)] flex items-center justify-center transition-transform group-hover:rotate-6">
                      <Icon className="w-5 h-5 text-black" strokeWidth={2.4} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-white/45">
                        {c.label}
                      </div>
                      <div className="text-white font-medium mt-0.5">
                        {c.handle}
                      </div>
                    </div>
                  </div>
                  <Send className="w-4 h-4 text-white/40 transition-all group-hover:text-[rgb(182,245,0)] group-hover:translate-x-1" />
                </a>
              );
            })}

            <div className="flex items-center gap-3 text-white/50 text-sm pt-2">
              <MapPin className="w-4 h-4 text-[rgb(182,245,0)]" />
              Available worldwide · Based in India
            </div>
          </motion.div>

          {/* FORM */}
          <motion.form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3 surface rounded-2xl p-7 sm:p-9"
          >
            <h3 className="font-display text-2xl font-semibold text-white mb-1">
              Send a quick message
            </h3>
            <p className="text-white/55 text-sm mb-7">
              Fill it in → we&apos;ll open WhatsApp with everything ready to send.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45 block mb-2">
                  Your Name
                </label>
                <input
                  data-testid="contact-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Stavan Bhore"
                  className="w-full bg-black border border-white/10 focus:border-[rgb(182,245,0)] outline-none rounded-xl px-4 py-3 text-white placeholder-white/30 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45 block mb-2">
                  Email (optional)
                </label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-black border border-white/10 focus:border-[rgb(182,245,0)] outline-none rounded-xl px-4 py-3 text-white placeholder-white/30 transition-colors"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs uppercase tracking-widest text-white/45 block mb-2">
                Project details
              </label>
              <textarea
                data-testid="contact-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="Tell us about your project, timeline & goals…"
                className="w-full bg-black border border-white/10 focus:border-[rgb(182,245,0)] outline-none rounded-xl px-4 py-3 text-white placeholder-white/30 transition-colors resize-none"
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit-btn"
              className="mt-6 btn-lime px-7 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 text-sm"
            >
              Send via WhatsApp <Send className="w-4 h-4" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
